import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Modal,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Close, LinkOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import {
  useGetDialogueById,
  useShareDialogue,
} from "src/views/assistant/AssistantServices";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 500,
  width: "100%",
  py: 2,
  px: 2,
};

export interface ChatShareModalProps {
  dialogueId: number;
  /**
   * Absolute URL of the chat being shared.
   *
   * Required rather than defaulted to `window.location.href`: the surfaces
   * offering this are no longer only the chat's own page. A history row or a
   * project header would copy the URL of the page the reader is standing on,
   * which is a different chat or no chat at all, and the copied link would
   * look right while going somewhere else.
   */
  shareUrl: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Turn link sharing on or off for one chat, and copy its link.
 *
 * Owns the whole overlay, not just its card, so a caller supplies an anchor
 * and two pieces of state and nothing else. Owner gating belongs to the
 * caller — see `useIsDialogueOwner`.
 */
const ChatShareModal = ({
  dialogueId,
  shareUrl,
  open,
  onClose,
}: ChatShareModalProps) => {
  const [isCopySuccess, setIsCopySuccess] = useState<boolean>(false);

  const { data: dialogue } = useGetDialogueById({ dialogueId });
  const isShared = Boolean(dialogue?.is_shared);

  const { mutate: shareDialogue, isLoading: isUpdating } = useShareDialogue();

  const handleToggleShare = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setIsCopySuccess(false);
    shareDialogue({ dialogueId, isShared: checked });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopySuccess(true);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // The confirmation is about one press of Copy link. This component stays
  // mounted between opens on a host that renders it unconditionally, so a
  // reopen would otherwise start on "Link Copied".
  useEffect(() => {
    if (!open) setIsCopySuccess(false);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div>
        <Card sx={style} variant="outlined" data-testid="chat-share-modal">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                fontWeight="bold"
              >
                Share link to chat
              </Typography>
              <IconButton
                onClick={onClose}
                aria-label="Close"
                data-testid="chat-share-modal-close"
                data-attr="assistant:btn:close-share-modal"
              >
                <Close />
              </IconButton>
            </Stack>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={isShared}
                  onChange={handleToggleShare}
                  disabled={isUpdating}
                  data-attr="assistant:toggle:share-dialogue"
                />
              }
              label={
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography>
                    Anyone with the link can view this chat
                  </Typography>
                  {isUpdating && <CircularProgress size={16} />}
                </Stack>
              }
            />

            <Alert severity="success" hidden={!isCopySuccess} sx={{ mt: 1 }}>
              Link Copied
            </Alert>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 1 }}>
              {isShared
                ? "Anyone signed in with this link can view your full conversation. Turn off sharing to revoke access."
                : "Sharing is off — this chat is private to you. Turn on sharing to let anyone with the link view it."}
            </Typography>
          </CardContent>

          <CardActions>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ width: "100%" }}
            >
              <TextField
                hiddenLabel
                disabled
                fullWidth
                variant="outlined"
                id="filled-hidden-label-small"
                size="small"
                sx={{
                  height: "100%",
                  "& .MuiInputBase-input": { backgroundColor: "#F3F6F9" },
                }}
                value={shareUrl}
                inputProps={{ "data-testid": "chat-share-url" }}
              />
              <Button
                disableElevation
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCopyLink}
                disabled={!isShared}
                startIcon={<LinkOutlined />}
                sx={{ flexShrink: 0 }}
                data-testid="chat-share-copy-link"
                data-attr="assistant:btn:copy-share-link"
              >
                Copy link
              </Button>
            </Stack>
          </CardActions>
        </Card>
      </div>
    </Modal>
  );
};

export default ChatShareModal;
